import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { Venue } from "../types";

// ── Pelipaikan valinta tapahtumalle ───────────────────────────
//
// Klubin asetuksissa määritetyt paikat (nimi + osoite) tarjotaan
// alasvetovalikosta. "Muu paikka" avaa vapaat kentät, jotta vierasturnaus
// tai kertaluonteinen paikka onnistuu edelleen. Jos klubilla ei ole yhtään
// paikkaa, näytetään suoraan vapaat kentät — kuten ennen tätä muutosta.

export interface VenueValue {
  venue: string;
  address: string;
}

const OTHER = "__other";

/** Klubin pelipaikat klubidokumentista. Tyhjä lista kunnes ladattu. */
export function useClubVenues(clubId: string): Venue[] {
  const [venues, setVenues] = useState<Venue[]>([]);
  useEffect(() => {
    let alive = true;
    getDoc(doc(db, "clubs", clubId)).then(snap => {
      if (!alive) return;
      const raw = (snap.data()?.venues as Venue[] | undefined) ?? [];
      setVenues(raw.filter(v => v && typeof v.name === "string" && v.name.trim()));
    }).catch(() => { /* ilman listaa vapaat kentät riittävät */ });
    return () => { alive = false; };
  }, [clubId]);
  return venues;
}

interface Props {
  clubId: string;
  value: VenueValue;
  onChange: (v: VenueValue) => void;
  lang?: "fi" | "en";
  inputStyle: React.CSSProperties;
}

const VenuePicker: React.FC<Props> = ({ clubId, value, onChange, lang = "fi", inputStyle }) => {
  const venues = useClubVenues(clubId);
  const [other, setOther] = useState(false);

  const known = venues.find(v => v.name === value.venue);
  // Vapaat kentät: ei listaa, "Muu paikka" valittu, tai tapahtumalla on
  // nimi jota listasta ei löydy (vanha tapahtuma tai poistettu paikka).
  const showInputs = venues.length === 0 || other || (!!value.venue && !known);

  function pick(e: React.ChangeEvent<HTMLSelectElement>) {
    const v = e.target.value;
    if (v === OTHER) { setOther(true); onChange({ venue: "", address: "" }); return; }
    setOther(false);
    const hit = venues.find(x => x.name === v);
    onChange(hit ? { venue: hit.name, address: hit.address || "" } : { venue: "", address: "" });
  }

  return (
    <div>
      {venues.length > 0 && (
        <select
          value={known ? known.name : (showInputs ? OTHER : "")}
          onChange={pick}
          style={{ ...inputStyle, appearance: "auto" as React.CSSProperties["appearance"] }}
        >
          <option value="">{lang === "fi" ? "Valitse pelipaikka…" : "Choose a venue…"}</option>
          {venues.map(v => (
            <option key={v.name} value={v.name}>
              {v.name}{v.address ? ` — ${v.address}` : ""}
            </option>
          ))}
          <option value={OTHER}>{lang === "fi" ? "Muu paikka…" : "Other venue…"}</option>
        </select>
      )}
      {showInputs && (
        <>
          <input
            value={value.venue}
            onChange={e => onChange({ ...value, venue: e.target.value })}
            placeholder={lang === "fi" ? "Paikka / Halli" : "Venue / Hall"}
            style={inputStyle}
          />
          <input
            value={value.address}
            onChange={e => onChange({ ...value, address: e.target.value })}
            placeholder={lang === "fi" ? "Osoite (valinnainen)" : "Address (optional)"}
            style={inputStyle}
          />
        </>
      )}
    </div>
  );
};

export default VenuePicker;
